import React, { useEffect } from 'react';
import { Container, Section } from 'react-bulma-components';
import { usePageTitle } from '../../hooks/usePageTitle';
import styles from './TermsAndConditions.module.scss';
import SupportEmailLink from '../../components/SupportEmailLink/SupportEmailLink';
import DiscordInviteButton from '../../components/DiscordInviteButton/DiscordInviteButton';

const TermsAndConditions: React.FC = () => {
  usePageTitle({
    title: 'Terms & Conditions | OnePlay',
    description: 'This is our Terms & Conditions, which helps explain our information practices.'
  });


  return (
    <Section className={styles.termsSection}>
      <Container>
        <div className={styles.content}>
          <h1 className={styles.title}>Terms of Use</h1>
          
          <div className={styles.effectiveDate}>
            Effective <span className={styles.date}>June 2024</span>
          </div>

          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Terms & Conditions</h2>
            <h2 className={styles.sectionTitle}>1. PARTIES</h2>
            <p className={styles.sectionContent}>
              The terms "We" / "Us" / "Our"/"Company" individually and collectively refer to Rainbox Media Pvt. Ltd. and its brand name 'Oneplay' and the terms "You"/ "Guest User"/ "Registered User"/ "Subscribed User"/ "User" refer to the users.
            </p>
          </div>

          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>2. LEGALLY BINDING AGREEMENT</h2>
            <p className={styles.sectionContent}>
              This user agreement ("<strong>User Agreement</strong>") is an electronic record in terms of Information Technology Act, 2000 ("<strong>Act</strong>") and rules thereunder as applicable and the amended provisions pertaining to electronic records in various statutes as amended by the Information Technology(Amendment) Act, 2008, and such other applicable amendments, from time to time. This User Agreement is generated by a computer system and does not require any physical or digital signatures. This Agreement states the Terms and Conditions under which you may use One Play which includes cloud streaming for games and cloud PC services accessible through mobile application and other services including all features and functionalities, the application, and user interfaces and software and websites/ web pages associated with the Service, whether accessed via computer, mobile device or other technology, manner or means (collectively "One Play Services/ Services"). Please read this page carefully as it is deemed to be a legally binding agreement. If you do not accept the Terms and Conditions stated here, we would request you to not use One Play. We reserve our right to revise these Terms and Conditions at any time by updating this post. You should visit this page periodically to re-appraise yourself of the Terms and Conditions, because they are binding on all users of One Play Services.
            </p>
          </div>

          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>3. ELIGIBILITY</h2>
            <p className={styles.sectionContent}>
              By using One Play as a guest, or registering for or subscribing to or by accessing/using the One Play Services, you represent, warrant and expressly agree that you are an adult, and that you will be responsible for ensuring that any child/children authorized by you to use and access the One Play Services does/do so in accordance with these User Agreement. Those Users who are minors agree and confirm that they have valid parental consent for accessing and using our Services. Oneplay's Services are available only for users who have reached legal age in their jurisdiction or receive proper parental consent and guidance while accessing our Services. It is the responsibility of the holders of parental authority to determine which service is, or is not, appropriate for their minor child(ren) and to take the necessary precautions to restrict access to certain services and content. Users shall further comply with age restrictions applicable to games available through our Services.
            </p>
          </div>

          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>4. USE OF ONE PLAY SERVICES</h2>
            <p className={styles.sectionContent}>
              Presently, we offer cloud streaming and cloud PC services. Our cloud gaming service allows subscribers to stream games directly from their existing accounts on platforms such as Epic Games and Steam. Users can link their gaming accounts to their account with us, which facilitates the streaming of their purchased or owned games to various devices. This service provides the flexibility to play high-quality games on different devices without needing to download or install the games locally. Our cloud PC is an enhanced cloud service that offers cloud PC functionalities and provides subscribers with access to powerful virtual computers hosted in the cloud. This allows users to perform a wide range of computing tasks remotely. Our cloud PC service enables users to download and play games not available on our platform, use creative applications, perform video editing, and engage in various other computing activities. Each User shall use One Play Services only for personal non-commercial and non-profit use. In the event of any breach of this clause or otherwise, we may limit the availability of our Services in whole or in part, block or deny access to any person, area or jurisdiction at any time and without notice.
            </p>
          </div>

          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>5. ACCESS TO ONE PLAY SERVICE</h2>
            <p className={styles.sectionContent}>
              One Play Services can be accessed for free as well as through a subscription based model. You can access these services as a guest user ("<strong>Guest User</strong>") or as a Registered User. As a Guest User you can stream limited games and limited features only available on One Play Services. As a Guest User, you agree to be bound by the terms of this Agreement and our Privacy Policy, to the extent they are applicable to you.
            </p>
          </div>

          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>6. USER ACCOUNT</h2>
            <p className={styles.sectionContent}>
              <strong>a.</strong> Some of One Play Services, can be availed only after creating an account ("<strong>Registered User</strong>"). To become a Registered User you will be required to sign up and create your user account which will contain personal information. It implies providing your phone number. Your mobile number must be verified by you to proceed further through one-time-password which will be sent to you simultaneously via SMS. You will be further asked to provide us with your date of birth to enable your use of age appropriate content on the One Play Services.
            </p>
            <p className={styles.sectionContent}>
              <strong>b.</strong> Upon creation of your user account, you will be assigned a system generated username. If you wish to change your username or add more details to your account like your name, email ID, password, you may do so in the settings section.
            </p>
            <p className={styles.sectionContent}>
              <strong>c.</strong> When creating an account, you represent that:
            </p>
            <ul className={styles.list}>
              <li>All information you submit is truthful, current, complete and accurate and; you will maintain the accuracy of such information.</li>
              <li>Your use of One Play Services is not against any applicable law or regulation in your jurisdiction and in our jurisdiction (India).</li>
              <li>You have read and agree to the terms of our Privacy Policy.</li>
            </ul>
          </div>

          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>7. SUBSCRIPTION, PAYMENT AND REFUND</h2>
            <p className={styles.sectionContent}>
              OnePlay offers various subscription plans for access to our Services. Subscription fees, payment terms, and refund policies are detailed in our Refund Policy. By subscribing to our Services, you agree to pay the applicable fees and comply with our payment and refund terms.
            </p>
          </div>

          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>8. ACCOUNT DEACTIVATION AND DELETION</h2>
            <p className={styles.sectionContent}>
              You may deactivate or delete your account at any time through your account settings. We reserve the right to suspend or terminate accounts that violate these Terms and Conditions or engage in prohibited activities. Upon account termination, your access to OnePlay Services will be revoked.
            </p>
          </div>

          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>9. USE OF CONTENT</h2>
            <p className={styles.sectionContent}>
              All content available through OnePlay Services, including games, software, and other materials, is protected by copyright and other intellectual property laws. You may not copy, modify, distribute, or create derivative works from any content without proper authorization.
            </p>
          </div>

          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>10. PROHIBITED ACTIVITIES</h2>
            <p className={styles.sectionContent}>
              You agree not to engage in any of the following prohibited activities:
            </p>
            <ul className={styles.list}>
              <li>Violating any applicable laws or regulations</li>
              <li>Infringing on intellectual property rights</li>
              <li>Transmitting harmful code or malware</li>
              <li>Attempting to gain unauthorized access to our systems</li>
              <li>Interfering with or disrupting the Services</li>
              <li>Using the Services for any illegal or unauthorized purpose</li>
            </ul>
          </div>

          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>11. ONEPLAY STORE</h2>
            <p className={styles.sectionContent}>
              OnePlay Store is our digital marketplace where you can purchase game keys, gift cards, and other digital products. All purchases are subject to our Refund Policy and Shipping & Delivery Policy.
            </p>
          </div>

          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>12. DELIVERY AND ACTIVATION</h2>
            <p className={styles.sectionContent}>
              Digital products purchased through OnePlay Store are delivered via email after successful payment. Delivery typically occurs within 24 hours, though processing may take up to 48 hours in some cases. Please refer to our Shipping & Delivery Policy for detailed information.
            </p>
          </div>

          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>13. REFUND POLICY</h2>
            <p className={styles.sectionContent}>
              Our refund policy is detailed in our Refund Policy page. Please review it carefully before making a purchase. Generally, refunds are available for unviewed product keys or in cases of invalid or previously used keys.
            </p>
          </div>

          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>14. LIMITATION OF LIABILITY</h2>
            <p className={styles.sectionContent}>
              OnePlay shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use or inability to use our Services. Our total liability shall not exceed the amount you paid for the Services in the 12 months preceding the claim.
            </p>
          </div>

          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>15. MODIFICATIONS TO TERMS</h2>
            <p className={styles.sectionContent}>
              We reserve the right to modify these Terms and Conditions at any time. We will notify users of significant changes, but it is your responsibility to review these terms periodically. Continued use of our Services after changes constitutes acceptance of the modified terms.
            </p>
          </div>

          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>16. CONTACT INFORMATION</h2>
            <p className={styles.sectionContent}>
              For questions or concerns regarding these Terms and Conditions, please contact us at <a href="mailto:grievance@oneplay.in" className={styles.emailLink}>grievance@oneplay.in</a>
            </p>
          </div>

          <div className={styles.agreement}>
            By using OnePlay Services, you confirm that you have read, understood, and agreed to the terms outlined in these Terms and Conditions.
          </div>

          <div className={styles.section}>
            <div className={styles.stepContent}>
              <h3 className={styles.stepTitle}>OnePlay Support</h3>
              <div className={styles.stepDescription}>
                <div style={{ marginBottom: '12px' }}>
                  For any assistance please contact us via email at <SupportEmailLink /> or join our Discord channel:
                </div>
                <DiscordInviteButton />
              </div>
            </div>
          </div>
        </div>
      </Container>
    </Section>
  );
};

export default TermsAndConditions;

